/**
 * Mission Validation Utility
 *
 * Validates missions and waypoints for errors, warnings, and best practices
 */

import type { Mission } from '../types/mission.types';
import type { Waypoint } from '../types/waypoint.types';
import type { ValidationResult, MissionValidation } from '../types/settings.types';
import { ValidationLevel } from '../types/settings.types';
import { MavCommand, isCommandSupportedForVehicle, COMMAND_INFO, type MavCommandParam } from '@/constants/mavlink-commands';
import { VehicleType } from '../types/mission.types';
import { MISSION_DEFAULTS } from '@/constants/mission-defaults';

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Calculate total mission distance
 */
function calculateMissionDistance(waypoints: Waypoint[]): number {
  if (waypoints.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const wp1 = waypoints[i];
    const wp2 = waypoints[i + 1];
    totalDistance += calculateDistance(
      wp1.latitude,
      wp1.longitude,
      wp2.latitude,
      wp2.longitude
    );
  }

  return totalDistance;
}

/**
 * Estimate mission flight time in seconds
 */
function estimateMissionTime(mission: Mission): number {
  if (mission.waypoints.length < 2) return 0;

  const speed = mission.defaultSpeed || 5; // m/s
  const distance = calculateMissionDistance(mission.waypoints);

  // Add time for altitude changes and hold times
  let holdTime = 0;
  mission.waypoints.forEach((wp) => {
    // param1 for NAV_WAYPOINT and similar commands is hold time
    if (
      wp.command === MavCommand.NAV_WAYPOINT ||
      wp.command === MavCommand.NAV_LOITER_TIME
    ) {
      holdTime += wp.param1;
    }
  });

  return distance / speed + holdTime;
}

/**
 * Validate individual waypoint
 */
function validateWaypoint(
  waypoint: Waypoint,
  mission: Mission,
  index: number
): ValidationResult[] {
  const results: ValidationResult[] = [];

  // Validate coordinates
  if (waypoint.latitude < -90 || waypoint.latitude > 90) {
    results.push({
      id: `wp-${waypoint.id}-lat`,
      level: ValidationLevel.ERROR,
      message: `Waypoint #${index + 1}: Invalid latitude ${waypoint.latitude}`,
      affectedItems: [waypoint.id],
      suggestedFix: 'Latitude must be between -90 and 90 degrees',
      timestamp: new Date(),
    });
  }

  if (waypoint.longitude < -180 || waypoint.longitude > 180) {
    results.push({
      id: `wp-${waypoint.id}-lon`,
      level: ValidationLevel.ERROR,
      message: `Waypoint #${index + 1}: Invalid longitude ${waypoint.longitude}`,
      affectedItems: [waypoint.id],
      suggestedFix: 'Longitude must be between -180 and 180 degrees',
      timestamp: new Date(),
    });
  }

  // Validate altitude
  if (waypoint.altitude < MISSION_DEFAULTS.minAltitude) {
    results.push({
      id: `wp-${waypoint.id}-alt-min`,
      level: ValidationLevel.ERROR,
      message: `Waypoint #${index + 1}: Altitude ${waypoint.altitude}m is below minimum`,
      affectedItems: [waypoint.id],
      suggestedFix: `Set altitude to at least ${MISSION_DEFAULTS.minAltitude}m`,
      timestamp: new Date(),
    });
  }

  if (waypoint.altitude > MISSION_DEFAULTS.maxAltitude) {
    results.push({
      id: `wp-${waypoint.id}-alt-max`,
      level: ValidationLevel.WARNING,
      message: `Waypoint #${index + 1}: Altitude ${waypoint.altitude}m is very high`,
      affectedItems: [waypoint.id],
      suggestedFix: `Consider reducing altitude below ${MISSION_DEFAULTS.maxAltitude}m`,
      timestamp: new Date(),
    });
  }

  // Validate command compatibility with vehicle type
  if (!isCommandSupportedForVehicle(waypoint.command, mission.vehicleType)) {
    results.push({
      id: `wp-${waypoint.id}-cmd-compat`,
      level: ValidationLevel.ERROR,
      message: `Waypoint #${index + 1}: Command not supported for ${mission.vehicleType}`,
      affectedItems: [waypoint.id],
      suggestedFix: 'Select a different command type compatible with your vehicle',
      timestamp: new Date(),
    });
  }

  // Validate parameters based on command type
  const commandInfo = COMMAND_INFO[waypoint.command as MavCommand];
  if (commandInfo) {
    commandInfo.params.forEach((paramInfo: MavCommandParam) => {
      const paramValue =
        paramInfo.index === 1
          ? waypoint.param1
          : paramInfo.index === 2
          ? waypoint.param2
          : paramInfo.index === 3
          ? waypoint.param3
          : waypoint.param4;

      // Check min/max constraints
      if (paramInfo.min !== undefined && paramValue < paramInfo.min) {
        results.push({
          id: `wp-${waypoint.id}-param${paramInfo.index}-min`,
          level: ValidationLevel.WARNING,
          message: `Waypoint #${index + 1}: ${paramInfo.name} is below minimum (${paramInfo.min})`,
          affectedItems: [waypoint.id],
          suggestedFix: `Set ${paramInfo.name} to at least ${paramInfo.min}`,
          timestamp: new Date(),
        });
      }

      if (paramInfo.max !== undefined && paramValue > paramInfo.max) {
        results.push({
          id: `wp-${waypoint.id}-param${paramInfo.index}-max`,
          level: ValidationLevel.WARNING,
          message: `Waypoint #${index + 1}: ${paramInfo.name} exceeds maximum (${paramInfo.max})`,
          affectedItems: [waypoint.id],
          suggestedFix: `Set ${paramInfo.name} to at most ${paramInfo.max}`,
          timestamp: new Date(),
        });
      }
    });
  }

  return results;
}

/**
 * Validate mission structure
 */
function validateMissionStructure(mission: Mission): ValidationResult[] {
  const results: ValidationResult[] = [];
  const waypoints = mission.waypoints;

  // Check minimum waypoints
  if (waypoints.length === 0) {
    results.push({
      id: 'mission-empty',
      level: ValidationLevel.ERROR,
      message: 'Mission has no waypoints',
      affectedItems: [],
      suggestedFix: 'Add at least one waypoint to the mission',
      timestamp: new Date(),
    });
    return results; // Can't validate further
  }

  if (waypoints.length === 1) {
    results.push({
      id: 'mission-single-wp',
      level: ValidationLevel.WARNING,
      message: 'Mission has only one waypoint',
      affectedItems: [waypoints[0].id],
      suggestedFix: 'Add more waypoints to create a complete flight path',
      timestamp: new Date(),
    });
  }

  // Check for takeoff command (for copters/VTOL)
  if (
    mission.vehicleType === VehicleType.COPTER ||
    mission.vehicleType === VehicleType.VTOL
  ) {
    const hasTakeoff = waypoints.some(
      (wp) => wp.command === MavCommand.NAV_TAKEOFF || wp.command === MavCommand.NAV_VTOL_TAKEOFF
    );
    if (!hasTakeoff) {
      results.push({
        id: 'mission-no-takeoff',
        level: ValidationLevel.WARNING,
        message: 'Mission does not include a takeoff command',
        affectedItems: [],
        suggestedFix: 'Add NAV_TAKEOFF as the first waypoint',
        timestamp: new Date(),
      });
    }
  }

  // Check for land command
  const hasLand = waypoints.some(
    (wp) =>
      wp.command === MavCommand.NAV_LAND ||
      wp.command === MavCommand.NAV_VTOL_LAND ||
      wp.command === MavCommand.NAV_RETURN_TO_LAUNCH
  );
  if (!hasLand) {
    results.push({
      id: 'mission-no-land',
      level: ValidationLevel.WARNING,
      message: 'Mission does not include a landing command',
      affectedItems: [],
      suggestedFix: 'Add NAV_LAND or NAV_RETURN_TO_LAUNCH at the end',
      timestamp: new Date(),
    });
  }

  // Check for large altitude changes between consecutive waypoints
  for (let i = 0; i < waypoints.length - 1; i++) {
    const wp1 = waypoints[i];
    const wp2 = waypoints[i + 1];
    const altChange = Math.abs(wp2.altitude - wp1.altitude);

    if (altChange > 100) {
      // 100m threshold
      results.push({
        id: `wp-${wp1.id}-${wp2.id}-alt-change`,
        level: ValidationLevel.WARNING,
        message: `Large altitude change (${altChange.toFixed(0)}m) between waypoints #${i + 1} and #${i + 2}`,
        affectedItems: [wp1.id, wp2.id],
        suggestedFix: 'Consider adding intermediate waypoints for gradual altitude change',
        timestamp: new Date(),
      });
    }
  }

  // Check for very long distances between waypoints
  for (let i = 0; i < waypoints.length - 1; i++) {
    const wp1 = waypoints[i];
    const wp2 = waypoints[i + 1];
    const distance = calculateDistance(
      wp1.latitude,
      wp1.longitude,
      wp2.latitude,
      wp2.longitude
    );

    if (distance > 5000) {
      // 5km threshold
      results.push({
        id: `wp-${wp1.id}-${wp2.id}-distance`,
        level: ValidationLevel.WARNING,
        message: `Long distance (${(distance / 1000).toFixed(1)}km) between waypoints #${i + 1} and #${i + 2}`,
        affectedItems: [wp1.id, wp2.id],
        suggestedFix: 'Verify coordinates and consider adding intermediate waypoints',
        timestamp: new Date(),
      });
    }
  }

  return results;
}

/**
 * Validate mission battery/range
 */
function validateMissionRange(mission: Mission): ValidationResult[] {
  const results: ValidationResult[] = [];

  // Calculate total distance
  const totalDistance = calculateMissionDistance(mission.waypoints);
  const totalTime = estimateMissionTime(mission);

  // Distance check (example: 10km warning, 20km error)
  if (totalDistance > 20000) {
    results.push({
      id: 'mission-range-error',
      level: ValidationLevel.ERROR,
      message: `Mission distance (${(totalDistance / 1000).toFixed(1)}km) exceeds safe range`,
      affectedItems: [],
      suggestedFix: 'Reduce mission waypoints or split into multiple missions',
      timestamp: new Date(),
    });
  } else if (totalDistance > 10000) {
    results.push({
      id: 'mission-range-warning',
      level: ValidationLevel.WARNING,
      message: `Mission distance (${(totalDistance / 1000).toFixed(1)}km) is quite long`,
      affectedItems: [],
      suggestedFix: 'Ensure battery capacity is sufficient for this distance',
      timestamp: new Date(),
    });
  }

  // Flight time check (example: 20min warning, 30min error)
  const timeMinutes = totalTime / 60;
  if (timeMinutes > 30) {
    results.push({
      id: 'mission-time-error',
      level: ValidationLevel.ERROR,
      message: `Estimated flight time (${timeMinutes.toFixed(0)}min) exceeds safe duration`,
      affectedItems: [],
      suggestedFix: 'Reduce mission complexity or battery requirements',
      timestamp: new Date(),
    });
  } else if (timeMinutes > 20) {
    results.push({
      id: 'mission-time-warning',
      level: ValidationLevel.WARNING,
      message: `Estimated flight time (${timeMinutes.toFixed(0)}min) is quite long`,
      affectedItems: [],
      suggestedFix: 'Verify battery capacity for extended flight',
      timestamp: new Date(),
    });
  }

  // Info: Mission statistics
  results.push({
    id: 'mission-stats',
    level: ValidationLevel.INFO,
    message: `Mission: ${(totalDistance / 1000).toFixed(1)}km, ~${timeMinutes.toFixed(0)}min, ${mission.waypoints.length} waypoints`,
    affectedItems: [],
    timestamp: new Date(),
  });

  return results;
}

/**
 * Main validation function
 */
export function validateMission(mission: Mission): MissionValidation {
  const results: ValidationResult[] = [];

  // Validate mission structure
  results.push(...validateMissionStructure(mission));

  // Validate each waypoint
  mission.waypoints.forEach((waypoint, index) => {
    results.push(...validateWaypoint(waypoint, mission, index));
  });

  // Validate range and battery
  if (mission.waypoints.length > 0) {
    results.push(...validateMissionRange(mission));
  }

  // Determine overall validity
  const hasErrors = results.some((r) => r.level === ValidationLevel.ERROR);
  const canUpload = !hasErrors && mission.waypoints.length > 0;

  return {
    isValid: !hasErrors,
    canUpload,
    results,
    lastValidated: new Date(),
  };
}

/**
 * Get validation summary counts
 */
export function getValidationSummary(validation: MissionValidation): {
  errors: number;
  warnings: number;
  info: number;
} {
  return {
    errors: validation.results.filter((r) => r.level === ValidationLevel.ERROR).length,
    warnings: validation.results.filter((r) => r.level === ValidationLevel.WARNING).length,
    info: validation.results.filter((r) => r.level === ValidationLevel.INFO).length,
  };
}
